import React from 'react';
import { Flex, Image, Divider, Space } from 'antd';
import { BsCreditCard2Back, BsEye } from 'react-icons/bs';
import { formatCurrency, getFormattedDate } from '@utils/utils';
import placeholderImage from 'assets/placeholder.png';
import { useSelector } from 'react-redux';
import Skeleton from '@common-web/skeleton';
import LinkWithPermission from '@common-web/link-with-permission';
import { GoLinkExternal } from 'react-icons/go';
import { strings } from '@constants/strings';
import styles from '../../../../../seller-center/orders/return-orders/index.module.scss';
import { ReturnModalSkeleton } from '@components-web/index';

const ReturnOrderDetailModalBody = ({ returnOrder, loading }) => {
  const currency = useSelector((state) => state.tenantData?.tenant?.currency ?? '');

  if (loading || !returnOrder) {
    return (
      <ReturnModalSkeleton />
    );
  }

  const {
    store,
    id,
    external_id,
    return_line_items = [],
    created_at,
    net_amount,
    shipping_charges,
    refund_proof,
    order_payment_method,
    return_reason,
    status,
  } = returnOrder;

  return (
    <Flex vertical gap={16} style={{ background: '#fff', borderRadius: 8 }}>
      <Flex vertical style={{ background: '#FFF5F5', padding: 16, borderRadius: 8 }} gap={16}>
        <Flex wrap="wrap" gap={30} style={{ width: '100%' }}>
          <Flex vertical>
            <span className="gray-500">{strings.order_id}</span>
            <span className="fw-600">{id || '-'}</span>
          </Flex>
          <Flex vertical>
            <span className="gray-500">{strings.store_id}</span>
            <span className="fw-600">{store?.id || '-'}</span>
          </Flex>
          <Flex vertical>
            <span className="gray-500">{strings.store_name}</span>
            <span className="fw-600">{store?.name || '-'}</span>
          </Flex>
          <Flex vertical>
            <span className="gray-500">{strings.external_id}</span>
            <span className="fw-600">{external_id || '-'}</span>
          </Flex>
          <Flex vertical>
            <span className="gray-500">{strings.payment_method}</span>
            <Flex align="center" gap={6}>
              <BsCreditCard2Back color="orangered" />
              <span className="fw-600">{order_payment_method?.name || '-'}</span>
            </Flex>
          </Flex>
          {status?.slug === 'refunded' && (
            <>
              <Flex vertical>
                <span className="gray-500">{strings.transaction_id}</span>
                <span className="fw-600">{refund_proof?.transaction_id ?? '-'}</span>
              </Flex>
              <Flex vertical>
                <span className="gray-500">{strings.transaction_proof}</span>
                <LinkWithPermission target="_blank" authorized={true} to={refund_proof?.images?.[0]?.sizes.document}>
                  <Flex className="fw-600" align="center">
                    {refund_proof?.images?.[0]?.sizes.document ? refund_proof.images[0].name : '-'}
                    <GoLinkExternal style={{ marginLeft: 4 }} />
                  </Flex>
                </LinkWithPermission>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      <Flex vertical gap={8} style={{ border: '1px solid #eee', padding: 16, borderRadius: 8 }}>
        <Flex justify="space-between" align="start" style={{ width: '100%' }}>
          <Flex gap={12} align={'center'}>
            <Image
              width={92}
              height={92}
              className="image-border"
              src={return_line_items?.[0]?.line_item?.cover_image?.sizes?.thumbnail ?? placeholderImage}
              alt="Loading..."
              loading="lazy"
              placeholder={<Skeleton width={92} height={92} borderRadius={4} />}
              wrapperClassName="flexShrink0"
              preview={{ mask: <BsEye /> }}
            />

            <Flex vertical gap={4}>
              <Space size={0} split={<Divider type="vertical" />} className={styles.productId}>
                <Flex gap={3}>
                  {strings.product_id} <span>{return_line_items?.[0]?.line_item?.product_id}</span>
                </Flex>
                <Flex gap={3}>
                  {strings.sku} <span>{return_line_items?.[0]?.line_item?.sku}</span>
                </Flex>
              </Space>
              <span className="fw-600">{return_line_items?.[0]?.line_item?.title}</span>
              <span className="fs-12 gray-500">{return_line_items?.[0]?.line_item?.category?.name ?? '-'}</span>
            </Flex>
          </Flex>

          <Flex vertical align="end">
            <span className="fw-600">
              {formatCurrency({
                price: return_line_items?.[0]?.line_item?.net_amount || 0,
                currency,
              })}
            </span>
            <span className="fw-600 gray-500">x{return_line_items?.[0]?.line_item?.quantity}</span>
          </Flex>
        </Flex>
        <Flex vertical>
          <span className="gray-500">
            Return Reason: <span style={{ fontWeight: 600, color: '#000' }}>{return_reason ?? '-'}</span>
          </span>
          <span className="gray-500">
            Requested Date:{' '}
            <span style={{ fontWeight: 600, color: '#000' }}>{getFormattedDate(created_at) || '-'}</span>
          </span>
        </Flex>

      </Flex>

      <Flex vertical gap={2}>
        <h4 className="fw-600">Summary</h4>
        <Flex justify="space-between">
          <span className="gray-500">Paid Amount</span>
          <span className="fw-600">{formatCurrency({ price: net_amount || 0, currency })}</span>
        </Flex>
      </Flex>

      <Divider style={{ margin: '12px 0' }} />

      <Flex justify="space-between" align="center">
        <h4 className="fw-600 m-0">Refund Amount</h4>
        <span style={{ color: 'var(--ant-color-primary)', fontWeight: 600 }}>
          {formatCurrency({ price: net_amount || 0, currency })}
        </span>
      </Flex>
    </Flex>
  );
};

export default ReturnOrderDetailModalBody;
